/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Calendar, MapPin, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { billetterieApi } from "@/lib/api";
import { useSeo } from "@/lib/seo";

function fmtDateLong(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function BilletPublic() {
  useSeo({
    title: "Mon billet | My Animation",
    description: "Votre billet d'entrée My Animation.",
    path: "/billet",
    noindex: true,
  });
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billetterieApi.publicTicket(code)
      .then(setTicket)
      .catch((e) => setErr(e?.response?.data?.detail || "Billet introuvable"))
      .finally(() => setLoading(false));
  }, [code]);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-[#DCB457]" /></div>;
  if (err || !ticket) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6 text-center">
        <div>
          <h2 className="font-serif-luxe text-3xl mb-3 gold-text">Billet introuvable</h2>
          <p className="text-white/65">{err}</p>
          <Link to="/billetterie" className="btn-ghost-gold mt-6 inline-flex">Voir les événements</Link>
        </div>
      </div>
    );
  }

  const ev = ticket.event || {};
  const grad = ev.bg_gradient || `linear-gradient(135deg, ${ev.primary_color || "#FF3D71"}, ${ev.secondary_color || "#7C3AED"})`;

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-4" data-testid="billet-page">
      <div className="w-full max-w-md">
        {/* Ticket card */}
        <div className="relative bg-white text-black rounded-2xl overflow-hidden shadow-2xl">
          {/* Stub line decoration */}
          <div className="absolute left-0 top-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 bg-[#050505] rounded-full" />
          <div className="absolute right-0 top-1/2 w-6 h-6 translate-x-1/2 -translate-y-1/2 bg-[#050505] rounded-full" />

          {/* Header colored */}
          <div className="relative h-32" style={{ background: grad }}>
            {ev.hero_image_url && <img src={ev.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
            <div className="absolute inset-0 bg-black/30" />
            <div className="relative h-full p-5 flex flex-col justify-between">
              <p className="text-xs uppercase tracking-[0.25em] text-[#f0d888]">{ticket.ticket_type_name}</p>
              <h1 className="font-serif-luxe text-2xl text-white drop-shadow">{ev.name}</h1>
            </div>
          </div>

          {/* Body */}
          <div className="p-6">
            <div className="flex items-center justify-center bg-white p-3 border-2 border-dashed border-gray-200 mb-5">
              <QRCodeSVG value={ticket.code} size={200} level="M" includeMargin={false} fgColor="#000000" />
            </div>
            <p className="text-center font-mono text-2xl tracking-[0.3em] font-bold">{ticket.code}</p>
            <p className="text-center text-gray-500 text-xs mt-1">Présentez ce code à l'entrée</p>

            <div className="mt-5 pt-4 border-t border-dashed border-gray-200 space-y-2 text-sm">
              {ev.date && <p className="flex items-center gap-2 text-gray-700"><Calendar size={13} /> {fmtDateLong(ev.date)}{ev.time && ` · ${ev.time}`}</p>}
              {(ev.venue_name || ev.city) && <p className="flex items-center gap-2 text-gray-700"><MapPin size={13} /> {ev.venue_name}{ev.city && `, ${ev.city}`}</p>}
              <p className="text-gray-700">Au nom de <strong>{ticket.buyer_name}</strong></p>
            </div>

            {/* Status badge */}
            <div className="mt-5">
              {ticket.is_used ? (
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs uppercase tracking-[0.18em] w-full justify-center">
                  <AlertCircle size={13} /> Déjà utilisé{ticket.used_at && ` le ${new Date(ticket.used_at).toLocaleString("fr-FR")}`}
                </div>
              ) : ticket.is_paid ? (
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs uppercase tracking-[0.18em] w-full justify-center">
                  <CheckCircle2 size={13} /> Billet valide
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 border border-gray-300 text-gray-700 text-xs uppercase tracking-[0.18em] w-full justify-center">
                  En attente de paiement
                </div>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-white/45 text-xs mt-5">My Animation · DJ événementiel · Billetterie en ligne</p>
      </div>
    </div>
  );
}
